import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  showAlert,
  showToast,
  formatDateWithDayjs,
  showAlertWithHtml
} from "../../utils/utils";
import useReckonApi from "../../hooks/useReckonApi";
import useMyobApi from "../../hooks/useMyobApi";
import useXeroApi from "../../hooks/useXeroApi";
import useQuickbooksApi from "../../hooks/useQuickbooksApi";
import AccountingWritebackFields from "./AccountingWritebackFields";
import {
  accSoftwareTab,
  alertActions,
  auPhNumber,
  commonErrMsg,
  mail,
  manualUpdate,
  monthlyLending,
  myAccount,
  nzPhNumber,
  nzText1
} from "../../locales/en";
import {
  MAILTO_INFO_EMAIL,
  PHONE_NO_AU,
  PHONE_NO_NZ,
  showToastType
} from "../../utils/constants/appConstants";
import PropTypes from "prop-types";
import Style from "./profile.module.scss";
import "./accountingsoftwaretab.scss";
import { INVOICE, LASTCONNECTEDSOFTWARE } from "../../route/paths";
import {
  setDisconnectConnectedSoftwareData,
  getConnectedSoftware
} from "../../redux/loginSlice";
import { useDispatch, useSelector } from "react-redux";

AccountingSoftwareTab.propTypes = {
  primaryUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  softwareDetails: PropTypes.object.isRequired,
  DisconnectSoftware: PropTypes.func.isRequired,
  CheckSoftwareDetails: PropTypes.func.isRequired
};

export default function AccountingSoftwareTab({
  primaryUserId,
  softwareDetails,
  DisconnectSoftware,
  CheckSoftwareDetails
}) {
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userManagement);
  const userAccountId = profile?.userAccountMeta?.userAccountId;
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ReckonRefresh } = useReckonApi();
  const { MyobRefresh } = useMyobApi();
  const { XeroRefresh } = useXeroApi();
  const { QuickBooksRefresh } = useQuickbooksApi();
  const navigate = useNavigate();

  const toggleToolTip = () => {
    setShowMenu((prevShowMenu) => !prevShowMenu);
  };

  const handleChangeSoftware = async () => {
    try {
      const result = await showAlert(
        accSoftwareTab?.handleChangeAlert?.title,
        accSoftwareTab?.handleChangeAlert?.subH2,
        "",
        alertActions?.Confirm,
        alertActions?.Cancel
      );

      if (result.isConfirmed) {
        await DisconnectSoftware(softwareDetails?.softwareId);
        navigate(INVOICE);
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  const handleDisconnect = async () => {
    try {
      const result = await showAlertWithHtml(
        accSoftwareTab?.handleDisconnectAlert?.title,
        accSoftwareTab?.handleDisconnectAlert?.subH2,
        "",
        accSoftwareTab?.handleDisconnectAlert?.disconnect,
        accSoftwareTab?.handleDisconnectAlert?.keepConnected,
        "accountingTabAlert"
      );

      if (result.isConfirmed) {
        await DisconnectSoftware(softwareDetails?.softwareId);
        showToast(accSoftwareTab?.handleDiscnnctMsg, showToastType.success);
        CheckSoftwareDetails();
        dispatch(setDisconnectConnectedSoftwareData({}));
        navigate(LASTCONNECTEDSOFTWARE);
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    } finally {
      setShowMenu(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      if (softwareDetails.accountingSoftwareType === "Reckon") {
        const response = await ReckonRefresh();
        showToast(response.data.message, showToastType.success);
      } else if (softwareDetails.accountingSoftwareType === "MYOB") {
        const response = await MyobRefresh();
        showToast(response.data.message, showToastType.success);
      } else if (softwareDetails.accountingSoftwareType === "Xero") {
        const response = await XeroRefresh();
        showToast(response.data.message, showToastType.success);
      } else if (softwareDetails.accountingSoftwareType === "QuickBooks") {
        const response = await QuickBooksRefresh();
        showToast(response.data.message, showToastType.success);
      }
    } catch (error) {
      if (error.response.status === 500) {
        navigate(INVOICE);
        showToast(error.response.data.errors[0], showToastType.error);
      } else {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSyncInvoice = async () => {
    try {
      setLoading(true);
      const payload = {
        userAccountId: userAccountId,
        isRefreshed: true
      };
      const response = await dispatch(getConnectedSoftware(payload)).unwrap();
      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
        navigate(INVOICE);
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || commonErrMsg.item2,
        showToastType.error
      );
    } finally {
      setLoading(false);
    }
  };

  const handelNavigate = () => {
    navigate(INVOICE);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  if (loading) {
    return (
      <div className="spinner-wrap bg-light">
        <div className="spinner-border custom-spinner-color" />
      </div>
    );
  }

  return (
    <div className="row accountingSoftwareTabMain" ref={menuRef}>
      {softwareDetails?.isConnected === true ? (
        <div className="row accountingSoftwareTab">
          <div
            className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center mb-3`}
          >
            <label className="col-12 col-md-5 pt-1 pb-1">
              {myAccount?.tabNames.accSoftware}
            </label>
            <span className="col-12 col-md-7 position-relative addTooltip">
              <input
                type="text"
                value={
                  softwareDetails.accountingSoftwareType === "Not connected"
                    ? manualUpdate
                    : softwareDetails.accountingSoftwareType
                }
                readOnly
              />
              <span onClick={toggleToolTip} className="optiontext">
                {accSoftwareTab?.options}
              </span>
              {showMenu && (
                <div className="addTooltip_menu">
                  <Link to={MAILTO_INFO_EMAIL}>
                    {accSoftwareTab?.reportAnIssue}
                  </Link>
                  <Link
                    to="#"
                    className={primaryUserId !== null ? "disable" : null}
                    onClick={
                      primaryUserId !== null ? null : handleChangeSoftware
                    }
                  >
                    {accSoftwareTab?.changeAccSoftware}
                  </Link>
                  {primaryUserId !== null ? (
                    <Link
                      to="#"
                      className={primaryUserId !== null ? "disable" : null}
                      onClick={primaryUserId !== null ? null : handleDisconnect}
                    >
                      {accSoftwareTab?.disconnect}
                    </Link>
                  ) : (
                    <Link
                      to="#"
                      className={
                        softwareDetails?.isConnected === false
                          ? "disable"
                          : null
                      }
                      onClick={
                        softwareDetails?.isConnected === false
                          ? null
                          : handleDisconnect
                      }
                    >
                      {accSoftwareTab?.disconnect}
                    </Link>
                  )}
                </div>
              )}
            </span>
          </div>

          <div
            className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center mb-3`}
          >
            <label htmlFor="status" className="col-12 col-md-5 pt-1 pb-1">
              {accSoftwareTab?.status}
            </label>
            <span className="col-12 col-md-7 position-relative" id="status">
              <input
                type="text"
                value={
                  softwareDetails?.isConnected === true
                    ? "Connected"
                    : "Disconnected"
                }
                readOnly
              />
              <span onClick={handleRefresh} className="optiontext">
                {accSoftwareTab?.refresh}
              </span>
            </span>
          </div>

          <div
            className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center mb-3`}
          >
            <label htmlFor="linkedAcc" className="col-12 col-md-5 pt-1 pb-1">
              {accSoftwareTab?.linkAcc}
            </label>
            <span className="col-12 col-md-7" id="linkedAcc">
              <input type="text" value={softwareDetails.legalName} disabled />
            </span>
          </div>

          <div
            className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center mb-3`}
          >
            <label htmlFor="lastSynced" className="col-12 col-md-5 pt-1 pb-1">
              {accSoftwareTab?.sync}
            </label>
            <span className="col-12 col-md-7" id="lastSynced">
              <input
                type="text"
                value={formatDateWithDayjs(
                  softwareDetails.lastSyncedAt,
                  "DD-MMM-YYYY HH:mm:ss"
                )}
                disabled
              />
            </span>
          </div>

          {softwareDetails.accountingSoftwareType === "Xero" && (
            <AccountingWritebackFields readOnly={primaryUserId !== null} />
          )}

          <div
            className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center my-4`}
          >
            <p>
              {`${accSoftwareTab?.haveQs} `}
              <Link to={MAILTO_INFO_EMAIL}>{mail}</Link>
              {` ${accSoftwareTab?.orCall}`}
              <br />
              {monthlyLending.au}
              <Link to={PHONE_NO_AU}>{auPhNumber}</Link>
              {` ${nzText1} `}
              <Link to={PHONE_NO_NZ}>{nzPhNumber}</Link>
            </p>
          </div>
          <div className="form-group d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSyncInvoice}
            >
              {accSoftwareTab?.refreshConnCtn}
            </button>
          </div>
        </div>
      ) : (
        <div className="isDisConnectedPart">
          <div
            className={`${Style.inputField} d-flex justify-content-center my-4`}
          >
            <label>{accSoftwareTab?.notConnectedMsg}</label>
          </div>
          <div className="form-group d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={handelNavigate}
            >
              {accSoftwareTab?.linkToSoftware}
            </button>
          </div>
          <div className="accFooter">
            <p>
              {`${accSoftwareTab?.haveQs} `}
              <Link to={MAILTO_INFO_EMAIL}>{mail}</Link>
              {` ${accSoftwareTab?.orCall}`}
              <br />
              {monthlyLending.au}
              <Link to={PHONE_NO_AU}>{auPhNumber}</Link>
              {` ${nzText1} `}
              <Link to={PHONE_NO_NZ}>{nzPhNumber}</Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
